'use client';

import { DestinationCGType, QueryCGType } from '@/types/graphql/root';
import WarningTriangle1614 from '@svg/poi-modal/warning_triangle_16_14.svg';
import PlusDefault2828 from '@svg/poi-modal/plus_default_28_28.svg';
import DeleteDefault2828 from '@svg/poi-modal/delete_default_28_28.svg';
import ReplaceDefault2222 from '@svg/poi-modal/replace_default_22_22.svg';
import HeartActive2422 from '@svg/poi-modal/heart_active_24_22.svg';
import HeartDefault2422 from '@svg/poi-modal/heart_default_24_22.svg';
import SpinnerLoader1818 from '@svg/common/spinner_loader_18_18.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDestinations, removeSlot, togglePoiSaved } from '@/utils/actions';
import { filterNullUndefined, getPoiType, getSortedDestinationList, PoiType } from '@/utils';
import Rating from './rating';
import { addToTripModalAtom, centerToastModalAtom, confirmModalAtom } from '@/atom/modal';
import { useAtomValue, useSetAtom } from 'jotai';
import PoiDetailGeneral from './poi-detail-general';
import PoiDetailMenus from './poi-detail-menus';
import PoiDetailAmenities from './poi-detail-amenities';
import PoiDetailReviews from './poi-detail-reviews';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { topDestSlotAtom } from '@/atom/trip';
import { historyStackAtom } from '@/atom/historyStack';
import { history } from '@/utils/history';
import SelectDayForDirectAddDestModal from '../trip/select-day-for-direct-add-dest-modal';
import useToggleSavedQueryCache from '@/app/hooks/useToggleSavedQueryCache';

const PRICES = [
  { name: '$', value: '1' },
  { name: '$$', value: '2' },
  { name: '$$ ~ $$$', value: '3' },
  { name: '$$$', value: '4' },
  { name: '$$$$', value: '5' },
];

const businessStatusMap = new Map([
  ['CLOSED_PERMANENTLY', '폐업한 장소'],
  ['CLOSED_TEMPORARILY', '임시 휴무한 장소'],
]);

const dollarMap = new Map(PRICES.map((price) => [parseInt(price.value), price.name]));

export default function PoiDetailInfoContainer({
  poi,
  reviews,
  hasAddScheduleButton,
  poiModalInTripProps,
  isEmbedded,
}: {
  poi: QueryCGType['poi'] | null;
  reviews: QueryCGType['reviews'] | null;
  hasAddScheduleButton: boolean;
  poiModalInTripProps?: {
    isEditRole: boolean;
    iamEdit: boolean;
    editUser: string | null;
    tripId: string | null;
    tripDestinations: DestinationCGType[] | null;
    setTripDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
    scrollListToDestOnTop: (destId: string, dayNumber: number, isSmooth?: boolean, initStickyHeight?: number) => void;
  };
  isEmbedded?: boolean;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const from = useMemo(() => {
    return searchParams.get('from');
  }, [searchParams]);

  const isFromMain = useMemo(() => {
    return from === 'main';
  }, [from]);

  const setHistoryStack = useSetAtom(historyStackAtom);
  const setConfirmModal = useSetAtom(confirmModalAtom);
  const setAddToTripModal = useSetAtom(addToTripModalAtom);
  const { toggleSaved } = useToggleSavedQueryCache();
  const topDestSlot = useAtomValue(topDestSlotAtom);

  const [selectDayForDirectAddDestModal, setSelectDayForDirectAddDestModal] = useState<boolean>(false);

  const [loadingToggleSaved, setLoadingToggleSaved] = useState(false);
  const [isSaved, setIsSaved] = useState<boolean>(poi?.saved ?? false);
  const [selectedTab, setSelectedTab] = useState<'general' | 'menus' | 'amenities' | 'reviews'>('general');

  const setCenterToastModal = useSetAtom(centerToastModalAtom);

  useEffect(() => {
    setIsSaved(poi?.saved ?? false);
  }, [poi]);

  const { isEditRole, editUser, iamEdit, tripDestinations, setTripDestinations, tripId, scrollListToDestOnTop } =
    poiModalInTripProps ?? {};

  const addAndSubtractAndReplaceButtons = useMemo(() => {
    if (from !== 'itinerary' && from !== 'map') {
      return {
        add: false,
        subtract: false,
        replace: false,
      };
    }
    if (!isEditRole) {
      return {
        add: false,
        subtract: false,
        replace: false,
      };
    }

    if (topDestSlot?.poi?.id === poi?.id) {
      return {
        add: false,
        subtract: true,
        replace: true,
      };
    } else {
      return {
        add: true,
        subtract: false,
        replace: false,
      };
    }
  }, [isEditRole, topDestSlot, poi, from]);

  const destInfo = useMemo(() => {
    if (topDestSlot?.poi?.id === poi?.id) {
      return {
        day: topDestSlot?.day,
        dest: topDestSlot,
      };
    }
    return null;
  }, [topDestSlot, poi]);

  const price = useMemo(() => {
    if (poi?.priceRange) return `${poi.priceRange.toLocaleString()}원`;
    else if (poi?.priceLevel && dollarMap.has(poi.priceLevel)) return `${dollarMap.get(poi.priceLevel)}, `;
    return;
  }, [poi?.priceRange, poi?.priceLevel]);

  const googleRating = useMemo(() => {
    return poi?.ratings?.find((rating) => rating?.provider === 'google');
  }, [poi?.ratings]);
  const tripadvisorRating = useMemo(() => {
    return poi?.ratings?.find((rating) => rating?.provider === 'tripadvisor');
  }, [poi?.ratings]);

  const menus = useMemo(() => {
    return filterNullUndefined(poi?.menu);
  }, [poi?.menu]);
  const hasMenu = useMemo(() => {
    return poi?.poiType && [PoiType.restaurant, PoiType.cafe].includes(poi.poiType as PoiType) && menus.length > 0;
  }, [poi?.poiType, menus]);

  const amenities = useMemo(() => {
    if (poi?.poiType !== PoiType.accomm) return [];
    return filterNullUndefined(poi?.amenities);
  }, [poi?.poiType, poi?.amenities]);

  const onClickToggleSaved = useCallback(async () => {
    if (!poi?.id) return;

    try {
      setLoadingToggleSaved(true);
      const togglePoiSaveResponse = await togglePoiSaved(poi.id);
      if (togglePoiSaveResponse.status === 200) {
        setIsSaved(togglePoiSaveResponse.togglePoiSave?.saved ?? false);
        toggleSaved(poi);
        document.dispatchEvent(
          new CustomEvent(`poi:saved:${poi.id}`, {
            detail: {
              saved: togglePoiSaveResponse.togglePoiSave?.saved ?? false,
            },
          }),
        );
      } else {
        console.error(togglePoiSaveResponse);
        setCenterToastModal({
          renderingTime: 1500,
          text: '로그인이\n필요 합니다.',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingToggleSaved(false);
    }
  }, [poi]);

  const onClickTabHeader = useCallback((targetId: string) => {
    const $target = document.getElementById(targetId);
    const $poiModal = document.querySelector('.poi-modal');

    if (!$target || !$poiModal) return;

    const $children = $poiModal.children;
    let reduceTop = 0;
    for (let i = 0; i < $children.length; i++) {
      if ($children[i].id === targetId) break;
      reduceTop += $children[i].clientHeight;
    }
    $poiModal.scrollTo({
      top: reduceTop - 44 + 5,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const $poiModal = document.querySelector('.poi-modal');
    if (!$poiModal) return;

    const targets: ['general', 'menus', 'amenities', 'reviews'] = ['general', 'menus', 'amenities', 'reviews'];
    const onScrollHandler = (e: Event) => {
      const $poiDetailTabs = document.getElementById('poi-detail-tabs');
      const triggerTop = $poiDetailTabs?.getBoundingClientRect().bottom ?? 0;
      for (const target of targets) {
        const $target = document.getElementById(`poi-detail-${target}`);
        if (!$target) continue;
        const top = $target.getBoundingClientRect().top;
        if (top <= triggerTop) {
          setSelectedTab(target);
        }
      }
    };

    $poiModal.addEventListener('scroll', onScrollHandler);
    return () => {
      $poiModal.removeEventListener('scroll', onScrollHandler);
    };
  }, []);

  const submitSelectDayForDirectAddDestModal = useCallback(
    async (day: number) => {
      if (!tripId || !poi) return;
      const destInDay = getSortedDestinationList(
        filterNullUndefined(tripDestinations?.filter((_d) => _d.day === day && !_d.accommType)),
      );

      let pos = 1;
      if (destInDay.length > 0) {
        pos = destInDay[destInDay.length - 1].pos + 1;
      }

      const dests = await addDestinations(
        tripId,
        [
          {
            day,
            placeId: poi?.placeId,
            pos,
            type: poi?.poiType,
          },
        ],
        pos,
        1,
      );
      if (dests?.data && setTripDestinations) {
        setTripDestinations((prev) => {
          const newDests = getSortedDestinationList(filterNullUndefined(dests.data));
          const prevIdList = prev.map((_d) => _d.id);
          const newIdList = newDests.map((_d) => _d.id).filter((_d) => !prevIdList.includes(_d));
          setTimeout(() => {
            const newId = newIdList[0];
            const newDest = newDests.find((_d) => _d.id === newId);
            if (newDest?.id && newDest.day && scrollListToDestOnTop)
              scrollListToDestOnTop(newDest.id, newDest.day, true);
          }, 100);

          return newDests;
        });
      }
      router.back();
    },
    [tripId, poi, tripDestinations],
  );

  const onClickAddToItinerary = useCallback(() => {
    if (iamEdit || editUser === null) {
      setSelectDayForDirectAddDestModal(true);
    } else {
      setCenterToastModal({
        text: `지금 ${editUser}님이 편집 중이에요!\n잠시만 기다려주세요.`,
        renderingTime: 2000,
        iconHidden: true,
      });
    }
  }, [poi, destInfo, tripId, iamEdit, editUser, submitSelectDayForDirectAddDestModal]);

  const getDestsWithRelation = useCallback(
    (dest: DestinationCGType) => {
      if (dest.accommType && dest.day) {
        if (dest.accommType === 'night') {
          const nextDay = dest.day + 1;
          const nextMorningDest = tripDestinations?.find(
            (_dest) => _dest.day === nextDay && _dest.accommType === 'morning',
          );
          return filterNullUndefined([dest, nextMorningDest]);
        } else if (dest.accommType === 'morning') {
          const prevDay = dest.day - 1;
          const prevNightDest = tripDestinations?.find(
            (_dest) => _dest.day === prevDay && _dest.accommType === 'night',
          );
          return filterNullUndefined([prevNightDest, dest]);
        } else {
          return [dest];
        }
      } else {
        return [dest];
      }
    },
    [tripDestinations],
  );

  const onClickDeleteDestination = useCallback(async () => {
    if (iamEdit || editUser === null) {
      if (!destInfo?.dest) return;
      const { dest } = destInfo;
      const relationDests = getDestsWithRelation(dest);

      history?.push(`${pathname}?${searchParams.toString()}`);
      setHistoryStack((prev) => [
        ...prev,
        {
          name: '슬롯 삭제 ConfirmModal 닫기 (from poi-detail-info-container.tsx)',
          afterAction: () => {
            setConfirmModal({ visible: false });
          },
        },
      ]);
      setConfirmModal({
        visible: true,
        title: '이 여행지를 삭제하시겠어요?',
        message: '일정표에서 영구적으로 삭제되며,\n복구 및 취소는 불가능해요.',
        onClose: () => router.back(),
        onOuterClick: () => router.back(),
        onConfirm: async () => {
          if (!tripId) return;
          router.back();
          router.back();
          await removeSlot(
            tripId,
            relationDests.map((_d) => {
              return {
                id: _d.id,
                day: _d.day,
                placeId: _d.poi?.placeId,
              };
            }),
          );
        },
      });
    } else {
      setCenterToastModal({
        text: `지금 ${editUser}님이 편집 중이에요!\n잠시만 기다려주세요.`,
        renderingTime: 2000,
        iconHidden: true,
      });
    }
  }, [destInfo, tripId, tripDestinations, getDestsWithRelation, editUser, iamEdit]);

  const onClickReplaceDestination = useCallback(
    async (dest: DestinationCGType) => {
      if (iamEdit || editUser === null) {
        router.back();
        await new Promise((resolve) => setTimeout(resolve, 100));
        document.dispatchEvent(
          new CustomEvent('onClickReplaceDestination', {
            detail: {
              destination: dest,
            },
          }),
        );
      } else {
        setCenterToastModal({
          text: `지금 ${editUser}님이 편집 중이에요!\n잠시만 기다려주세요.`,
          renderingTime: 2000,
          iconHidden: true,
        });
      }
    },
    [editUser, iamEdit],
  );

  const destInTrip = useMemo(() => {
    const destinations = tripDestinations?.filter((_d) => _d.poi?.id === poi?.id) ?? [];
    const uniqueDays = [...new Set(destinations.map((dest) => dest.day).filter(Boolean))];
    return uniqueDays;
  }, [tripDestinations, poi]);

  return (
    <>
      <div className='flex flex-col px-20pxr pt-24pxr pb-32pxr gap-16pxr'>
        <div className='flex flex-col gap-14pxr'>
          {poi ? (
            <h2 className='text-gray-800 heading-22-b'>{poi.name}</h2>
          ) : (
            <div className='rounded-2pxr w-100pxr h-29pxr skeleton-item' />
          )}

          <div className='flex gap-10pxr'>
            {destInTrip &&
              destInTrip.length > 0 &&
              destInTrip.map((_d) => {
                return (
                  <span
                    key={_d}
                    className='self-start text-white px-8pxr py-5pxr rounded-4pxr h-26pxr sub-heading-12-m'
                    style={{ backgroundColor: '#669AFFCC' }}
                  >
                    {_d}일차
                  </span>
                );
              })}
            {poi?.businessStatus && businessStatusMap.has(poi.businessStatus) && (
              <span className='flex flex-row items-center border gap-6pxr px-8pxr py-4pxr rounded-4pxr border-cancel'>
                <WarningTriangle1614 className='flex-none' />
                <p className='text-cancel sub-heading-14-m'>{businessStatusMap.get(poi.businessStatus)}</p>
              </span>
            )}
          </div>
        </div>

        {poi ? (
          <div className='flex flex-row items-center justify-around border border-gray-200 rounded-6pxr h-66pxr'>
            {hasAddScheduleButton && isFromMain && (
              <>
                <button
                  className='flex flex-col items-center flex-1 gap-2pxr py-8pxr px-12pxr'
                  onClick={() => {
                    setAddToTripModal({
                      poiId: poi.id,
                      placeId: poi.placeId,
                      type: poi.poiType,
                    });
                    router.push(`/poi/add-to-trip/${poi.id}?from=main`, {
                      scroll: false,
                    });
                  }}
                >
                  <PlusDefault2828 className='flex-none' />
                  <span className='text-gray-600 body-12-r'>일정추가</span>
                </button>
                <div className='border-l border-gray-200 w-1pxr h-48pxr' />
              </>
            )}
            {addAndSubtractAndReplaceButtons.add && (
              <>
                <button
                  className='flex flex-col items-center flex-1 gap-2pxr py-8pxr px-12pxr'
                  onClick={() => {
                    onClickAddToItinerary();
                  }}
                >
                  <PlusDefault2828 className='flex-none' />
                  <span className='text-gray-600 body-12-r'>장소 추가</span>
                </button>
                <div className='border-l border-gray-200 w-1pxr h-48pxr' />
              </>
            )}
            {addAndSubtractAndReplaceButtons.subtract && !isEmbedded && (
              <>
                <button
                  className='flex flex-col items-center flex-1 gap-2pxr py-8pxr px-12pxr'
                  onClick={() => {
                    onClickDeleteDestination();
                  }}
                >
                  <DeleteDefault2828 className='flex-none' />
                  <span className='text-gray-600 body-12-r'>장소 삭제</span>
                </button>
                <div className='border-l border-gray-200 w-1pxr h-48pxr' />
              </>
            )}
            <button
              className='flex flex-col items-center flex-1 gap-2pxr py-8pxr px-12pxr'
              onClick={() => onClickToggleSaved()}
            >
              {loadingToggleSaved && (
                <>
                  <SpinnerLoader1818 className='flex-none animate-spin' />
                </>
              )}
              {!loadingToggleSaved && isSaved && (
                <>
                  <HeartActive2422 className='flex-none' />
                  <span className='text-[#FD6464] body-12-r'>저장됨</span>
                </>
              )}
              {!loadingToggleSaved && !isSaved && (
                <>
                  <HeartDefault2422 className='flex-none' />
                  <span className='text-gray-600 body-12-r'>저장</span>
                </>
              )}
            </button>
            {addAndSubtractAndReplaceButtons.replace && !isEmbedded && (
              <>
                <div className='border-l border-gray-200 w-1pxr h-48pxr' />
                <button
                  className='flex flex-col items-center flex-1 gap-2pxr py-8pxr px-12pxr'
                  onClick={() => {
                    if (destInfo?.dest) {
                      onClickReplaceDestination(destInfo.dest);
                    }
                  }}
                >
                  <ReplaceDefault2222 className='flex-none' />
                  <span className='text-gray-600 body-12-r'>장소 교체</span>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className='flex flex-row items-center justify-around border border-gray-200 rounded-6pxr h-66pxr skeleton-item'></div>
        )}

        <div className='flex flex-col text-gray-600 gap-2pxr body-14-r'>
          {poi ? (
            <span className=''>{getPoiType(poi)}</span>
          ) : (
            <div className='rounded-2pxr w-80pxr h-21pxr skeleton-item' />
          )}

          {!poi && <div className='rounded-2pxr w-120pxr h-18pxr skeleton-item' />}
          {poi &&
            price &&
            poi.poiType !== '숙소' &&
            poi.priceRange !== null &&
            poi.priceRange !== undefined &&
            poi.priceRange > 0 && (
              <span className='flex flex-row items-center gap-4pxr body-12-r h-18pxr'>
                <span>예상비용(1인)</span>
                <span>{price}</span>
              </span>
            )}
        </div>
        <div className='flex flex-col gap-8pxr'>
          <Rating type='google' rating={googleRating ?? null} />
          <Rating type='tripadvisor' rating={tripadvisorRating ?? null} />
        </div>
      </div>

      <div className='w-full bg-gray-100 h-12pxr'></div>

      {!poi ? (
        <div className='sticky flex flex-row w-full bg-white border-b border-gray-200 h-44pxr gap-20pxr px-20pxr top-0'>
          <button className='button-14-m h-44pxr text-gray-800 border-b-2 border-gray-800'>기본정보</button>
          <button className='button-14-m h-44pxr text-gray-300'>메뉴</button>
          <button className='button-14-m h-44pxr text-gray-300'>편의시설</button>
          <button className='button-14-m h-44pxr text-gray-300'>리뷰</button>
        </div>
      ) : (
        <div
          id='poi-detail-tabs'
          className='sticky flex flex-row w-full bg-white border-b border-gray-200 h-44pxr gap-20pxr px-20pxr top-0'
        >
          <button
            className={`button-14-m h-44pxr border-gray-800 ${selectedTab === 'general' ? 'text-gray-800 border-b-2' : 'text-gray-300 border-b-0'}`}
            onClick={() => onClickTabHeader('poi-detail-general')}
          >
            기본정보
          </button>
          {hasMenu && (
            <button
              className={`button-14-m h-44pxr border-gray-800 ${selectedTab === 'menus' ? 'text-gray-800 border-b-2' : 'text-gray-300 border-b-0'}`}
              onClick={() => onClickTabHeader('poi-detail-menus')}
            >
              메뉴
            </button>
          )}
          {amenities.length > 0 && (
            <button
              className={`button-14-m h-44pxr border-gray-800 ${selectedTab === 'amenities' ? 'text-gray-800 border-b-2' : 'text-gray-300 border-b-0'}`}
              onClick={() => onClickTabHeader('poi-detail-amenities')}
            >
              편의시설
            </button>
          )}
          <button
            className={`button-14-m h-44pxr border-gray-800 ${selectedTab === 'reviews' ? 'text-gray-800 border-b-2' : 'text-gray-300 border-b-0'}`}
            onClick={() => onClickTabHeader('poi-detail-reviews')}
          >
            리뷰
          </button>
        </div>
      )}

      <PoiDetailGeneral poi={poi ?? null} />
      <PoiDetailMenus menus={menus} />
      {poi?.poiType === PoiType.accomm && <PoiDetailAmenities amenities={amenities} />}
      <PoiDetailReviews reviews={reviews ?? null} />

      <SelectDayForDirectAddDestModal
        useIsVisible={[selectDayForDirectAddDestModal, setSelectDayForDirectAddDestModal]}
        submitSelectDayForDirectAddDestModal={submitSelectDayForDirectAddDestModal}
      />
    </>
  );
}
